<template>
  <v-container>
    <!-- Header band -->
    <div class="topbar">
      <div class="topbar__row">
        <v-btn
          icon
          variant="text"
          :title="$filters.capitalize($t('machine.back'))"
          :to="{ name: 'solar_production' }"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="topbar__badge" :style="item ? { background: statusHex(item.worst_status_color) + '22', color: statusHex(item.worst_status_color) } : null">
          <v-icon size="22">mdi-solar-panel-large</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">
            {{ power_station_name || $filters.capitalize($t("machine.station")) }}
          </div>
          <div class="topbar__sub">
            <template v-if="item">
              {{ producers.length }} {{ $t("machine.producers", producers.length) }}
              &middot; {{ statusLabel(item.worst_status_color) }}
            </template>
            <template v-if="lastRefresh">
              &middot; {{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}
            </template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn-toggle
            v-model="range"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            class="range-toggle"
          >
            <v-btn v-for="r in ranges" :key="r.value" :value="r.value" size="small">{{ r.label }}</v-btn>
          </v-btn-toggle>
          <v-btn
            v-if="item && item.id"
            variant="tonal"
            size="small"
            prepend-icon="mdi-chart-areaspline"
            :href="grafanaSolarPowerStationUrl(item.id)"
            target="_blank"
            rel="noopener"
          >
            Grafana
          </v-btn>
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="refresh"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <template v-if="item">
      <!-- Station KPIs (several producers only: with one, the card already carries them) -->
      <v-row v-if="!single" dense class="kpi-row">
        <v-col cols="12" sm="6">
          <v-card flat class="kpi kpi--amber">
            <div class="kpi__badge"><v-icon size="20">mdi-flash</v-icon></div>
            <div class="min-width-0">
              <div class="kpi__label">{{ $filters.capitalize($t("machine.total_power")) }}</div>
              <div class="kpi__value">
                {{ $filters.formatFloat(item.total_power) }}
                <span class="kpi__unit">{{ $t("unity.kw") }}</span>
              </div>
              <div class="kpi__hint">
                <template v-if="totalCapacity > 0">
                  {{ $t("production.load_factor") }} {{ Math.round((item.total_power / totalCapacity) * 100) }} %
                </template>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card flat class="kpi kpi--blue">
            <div class="kpi__badge"><v-icon size="20">mdi-solar-panel</v-icon></div>
            <div class="min-width-0">
              <div class="kpi__label">{{ $filters.capitalize($t("production.capacity")) }}</div>
              <div class="kpi__value">
                {{ totalCapacity }}
                <span class="kpi__unit">{{ $t("unity.kw") }}c</span>
              </div>
              <div class="kpi__hint">
                {{ reportingCount }} / {{ producers.length }} {{ $t("production.reporting") }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Station chart: power per producer, from the Grafana station dashboard -->
      <v-card v-if="!single && item.id" flat class="graf-card mt-2">
        <div class="graf-card__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-chart-areaspline</v-icon>
          <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.station_chart")) }}</span>
        </div>
        <GrafanaFrame
          v-if="GRAFANA_EMBED_ENABLED"
          class="graf-frame graf-frame--station"
          :src="grafanaSolarPowerStationPanelUrl(item.id, GRAFANA_SOLAR_PANELS.station_per_producer, range)"
          :href="grafanaSolarPowerStationUrl(item.id, range)"
          :title="$t('machine.station_chart')"
          :hint="$t('machine.open_grafana')"
        ></GrafanaFrame>
        <a v-else :href="grafanaSolarPowerStationUrl(item.id, range)" target="_blank" rel="noopener" class="graf-placeholder">
          <v-icon size="32" color="primary">mdi-chart-areaspline</v-icon>
          <span>{{ $t("machine.open_grafana") }}</span>
          <span class="text-caption text-medium-emphasis">{{ $t("machine.embed_disabled") }}</span>
        </a>
      </v-card>

      <!-- Producers -->
      <v-row dense class="mt-1">
        <v-col v-for="producer in producers" :key="producer.id" cols="12" :lg="single ? 12 : 6">
          <v-card flat class="producer-card" :class="{ 'producer-card--single': single }">
            <!-- Header -->
            <div class="producer-head">
              <span class="producer-icon" :style="{ color: statusHex(producer.worst_status_color) }">
                <v-icon size="20">mdi-solar-panel</v-icon>
              </span>
              <div class="min-width-0 flex-grow-1">
                <div class="text-subtitle-1 font-weight-bold text-truncate">{{ producer.display_name }}</div>
                <div class="text-caption text-medium-emphasis">
                  <template v-if="producer.capacity_solar_panel">
                    {{ producer.capacity_solar_panel }} {{ $t("unity.kw") }}c
                  </template>
                  <template v-if="producer.ratio_solar_panel">
                    &middot; {{ $t("machine.ratio") }} {{ producer.ratio_solar_panel }} %
                  </template>
                </div>
              </div>
              <span class="status-pill" :style="{ color: statusHex(producer.worst_status_color) }">
                <span class="status-pill__dot"></span>
                {{ statusLabel(producer.worst_status_color) }}
              </span>
              <v-btn
                icon
                size="small"
                variant="text"
                class="ml-1"
                :title="$filters.capitalize($t('intervention_reports.label', 2))"
                :to="{ name: 'intervention_reports', query: { producer_id: producer.id } }"
              >
                <v-icon size="small">mdi-clipboard-text-outline</v-icon>
              </v-btn>
            </div>

            <!-- Latest values -->
            <div class="producer-stats">
              <div class="stat stat--wide">
                <div class="stat__label">{{ $filters.capitalize($t("telemetry.power")) }}</div>
                <div class="d-flex align-baseline justify-space-between">
                  <div class="stat__value stat__value--big">
                    {{ $filters.formatFloat(producer.latest_power) }}
                    <span class="stat__unit">{{ $t("unity.kw") }}</span>
                  </div>
                  <span v-if="loadFactor(producer) !== null" class="text-caption text-medium-emphasis">
                    {{ Math.round(loadFactor(producer) * 100) }} % {{ $t("production.load_factor") }}
                  </span>
                </div>
                <v-progress-linear
                  :model-value="(loadFactor(producer) || 0) * 100"
                  :color="statusColor(producer.worst_status_color)"
                  height="6"
                  rounded
                  bg-color="grey-lighten-3"
                  bg-opacity="1"
                  class="mt-1"
                ></v-progress-linear>
              </div>
              <div class="stat">
                <div class="stat__label">{{ $filters.capitalize($t("telemetry.radiance_vertical")) }}</div>
                <div class="stat__value">
                  {{ $filters.formatFloat(producer.latest_radiance_vert) }}
                  <span class="stat__unit">{{ $t("unity.wm2") }}</span>
                </div>
              </div>
              <div class="stat">
                <div class="stat__label">{{ $filters.capitalize($t("telemetry.radiance_horizontal")) }}</div>
                <div class="stat__value">
                  {{ $filters.formatFloat(producer.latest_radiance_hor) }}
                  <span class="stat__unit">{{ $t("unity.wm2") }}</span>
                </div>
              </div>
              <div class="stat">
                <div class="stat__label">{{ $filters.capitalize($t("production.last_data")) }}</div>
                <div class="stat__value">
                  <span
                    v-if="producer.time_delta_latest_telemetry >= 0"
                    class="fresh-chip"
                    :class="delayClass(producer.time_delta_latest_telemetry)"
                  >
                    {{ $t("production.minutes_ago", { n: producer.time_delta_latest_telemetry }) }}
                  </span>
                  <span v-else class="fresh-chip over_5h">{{ $t("machine.no_telemetry") }}</span>
                </div>
              </div>
            </div>

            <!-- Producer chart: power against the expected range, from the Grafana producer dashboard -->
            <div class="chart-block">
              <div class="d-flex align-center mb-1">
                <span class="text-caption text-medium-emphasis">{{ $filters.capitalize($t("machine.producer_chart")) }}</span>
              </div>
              <GrafanaFrame
                v-if="GRAFANA_EMBED_ENABLED"
                class="graf-frame"
                :src="grafanaSolarProducerPanelUrl(producer.id, GRAFANA_SOLAR_PANELS.producer_expected, range)"
                :href="grafanaSolarProducerUrl(producer.id, range)"
                :title="$t('machine.producer_chart')"
                :hint="$t('machine.open_grafana')"
              ></GrafanaFrame>
              <a v-else :href="grafanaSolarProducerUrl(producer.id, range)" target="_blank" rel="noopener" class="graf-placeholder graf-placeholder--small">
                <v-icon size="28" color="primary">mdi-chart-line</v-icon>
                <span>{{ $t("machine.open_grafana") }}</span>
              </a>
            </div>

            <!-- Rules -->
            <div v-if="ruleList(producer).length" class="d-flex flex-wrap align-center mt-3">
              <span class="text-caption text-medium-emphasis mr-2">{{ $filters.capitalize($t("machine.rules")) }}</span>
              <v-chip
                v-for="rule in ruleList(producer)"
                :key="rule.key"
                size="x-small"
                variant="tonal"
                :color="ruleColor(rule.status_color)"
                class="mr-1 mb-1"
              >
                {{ rule.name_rule }}
              </v-chip>
            </div>

            <!-- Telemetry table, folded by default -->
            <div v-if="producer.telemetries && producer.telemetries.length" class="mt-2">
              <v-btn
                size="x-small"
                variant="text"
                :prepend-icon="expanded[producer.id] ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                @click="toggleExpanded(producer.id)"
              >
                {{ expanded[producer.id] ? $t("machine.show_less") : $t("machine.show_all", { count: producer.telemetries.length }) }}
              </v-btn>
              <v-table v-if="expanded[producer.id]" density="compact" class="tel-table mt-1">
                <thead>
                  <tr>
                    <th>{{ $filters.capitalize($t("generic.hour", 1)) }}</th>
                    <th class="text-right">{{ $filters.capitalize($t("telemetry.power")) }}</th>
                    <th class="text-right">{{ $filters.capitalize($t("machine.theoretical")) }}</th>
                    <th class="text-right d-none d-md-table-cell">{{ $filters.capitalize($t("telemetry.radiance_vertical")) }}</th>
                    <th class="text-right d-none d-md-table-cell">{{ $filters.capitalize($t("telemetry.radiance_horizontal")) }}</th>
                    <th class="text-center">{{ $filters.capitalize($t("machine.status")) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tel in producer.telemetries" :key="tel.time" :class="tel.status_curve ? 'tel-ok' : 'tel-low'">
                    <td class="text-no-wrap">
                      {{ $filters.formatTime(tel.time) }}
                      <span class="text-caption text-medium-emphasis">(-{{ tel.time_delta }} min)</span>
                    </td>
                    <td class="text-right font-weight-medium">{{ $filters.formatFloat(tel.power) }}</td>
                    <td class="text-right text-medium-emphasis">{{ $filters.formatFloat(tel.theorical_power) }}</td>
                    <td class="text-right d-none d-md-table-cell">{{ $filters.formatFloat(tel.radiance_vert) }}</td>
                    <td class="text-right d-none d-md-table-cell">{{ $filters.formatFloat(tel.radiance_hor) }}</td>
                    <td class="text-center">
                      <v-icon v-if="tel.status_curve" size="small" color="success">mdi-check-circle</v-icon>
                      <v-icon v-else size="small" color="warning" :title="$filters.capitalize($t('production.below_expected'))">mdi-alert-circle</v-icon>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <!-- Alarms (only filled for alarm compatible producers) -->
            <div v-if="producer.alarms && producer.alarms.length" class="mt-3">
              <div class="text-caption text-medium-emphasis mb-1">{{ $filters.capitalize($t("alarms.label", 2)) }}</div>
              <v-alert
                v-for="alarm in producer.alarms"
                :key="alarm.id"
                density="compact"
                variant="tonal"
                :type="alarmType(alarm.alarm_code__criticality)"
                class="mb-1"
              >
                <div class="font-weight-medium">{{ alarm.alarm_code__name }} <span class="text-caption">({{ alarm.alarm_code__code }})</span></div>
                <div class="text-caption">{{ alarm.description }} &middot; {{ $filters.formatDateTime(alarm.time) }}</div>
              </v-alert>
            </div>

            <!-- Limitations -->
            <div v-if="producer.limitations && producer.limitations.length" class="mt-3">
              <div class="text-caption text-medium-emphasis mb-1">{{ $filters.capitalize($t("limitations.label", 2)) }}</div>
              <v-chip
                v-for="lim in producer.limitations"
                :key="lim.id"
                size="small"
                variant="tonal"
                prepend-icon="mdi-tortoise"
                class="mr-1 mb-1"
                :title="lim.comment"
              >
                {{ $filters.formatDateTime(lim.start) }}
                <template v-if="lim.end"> &rarr; {{ $filters.formatDateTime(lim.end) }}</template>
                <template v-if="lim.origin"> &middot; {{ lim.origin }}</template>
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-card v-else-if="!loading" flat class="pa-4 mt-2 text-medium-emphasis text-body-2">
      {{ $filters.capitalize($t("machine.no_station")) }}
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import {
  isNone,
  grafanaSolarProducerUrl,
  grafanaSolarPowerStationUrl,
  grafanaSolarProducerPanelUrl,
  grafanaSolarPowerStationPanelUrl,
  GRAFANA_SOLAR_PANELS,
  GRAFANA_EMBED_ENABLED
} from "@/functions.js";
import utils from "@/mixins/utils";
import GrafanaFrame from "@/components/GrafanaFrame.vue";

// Status colours sent by the backend (black, grey, red, yellow, green, white, unknown)
const STATUS_VUETIFY = {
  green: "success",
  yellow: "amber-darken-2",
  red: "error",
  grey: "grey",
  black: "grey-darken-4",
  white: "grey-lighten-1",
  unknown: "grey-lighten-1"
};
const STATUS_HEX = {
  green: "#2e7d32",
  yellow: "#e08a00",
  red: "#d32f2f",
  grey: "#757575",
  black: "#212121",
  white: "#9e9e9e",
  unknown: "#9e9e9e"
};
// Rule status index (backend) to a chip colour
const RULE_COLOR = { 2: "grey", 3: "error", 4: "amber-darken-2", 5: "success" };

export default {
  components: { GrafanaFrame },
  mixins: [utils],

  computed: {
    power_station_id() {
      return this.$route.query.power_station_id;
    },

    // Producers as a list with their id, sorted by name
    producers() {
      if (!this.item || !this.item.producers) {
        return [];
      }
      return Object.entries(this.item.producers)
        .map(([id, producer]) => ({ id: Number(id), ...producer }))
        .sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
    },

    totalCapacity() {
      return this.producers.reduce((acc, p) => acc + (Number(p.capacity_solar_panel) || 0), 0);
    },

    reportingCount() {
      return this.producers.filter((p) => p.time_delta_latest_telemetry >= 0).length;
    },

    single() {
      return this.producers.length === 1;
    },

    ranges() {
      const h = this.$t("limitations.duration_units.hour");
      const d = this.$t("limitations.duration_units.day");
      return [
        { value: "now-24h", label: `24 ${h}` },
        { value: "now-7d", label: `7 ${d}` },
        { value: "now-30d", label: `30 ${d}` }
      ];
    }
  },

  methods: {
    grafanaSolarProducerUrl,
    grafanaSolarPowerStationUrl,
    grafanaSolarProducerPanelUrl,
    grafanaSolarPowerStationPanelUrl,

    statusColor(color) {
      return STATUS_VUETIFY[color] || "grey-lighten-1";
    },

    statusHex(color) {
      return STATUS_HEX[color] || STATUS_HEX.unknown;
    },

    statusLabel(color) {
      const key = `machine.status_${color}`;
      const label = this.$te(key) ? this.$t(key) : this.$t("machine.status_unknown");
      return this.$filters.capitalize(label);
    },

    ruleColor(index) {
      return RULE_COLOR[index] || "grey";
    },

    // Rules arrive as a list of {index: {name_rule, status_color}} objects
    ruleList(producer) {
      const rules = [];
      (producer.rules || []).forEach((rule) => {
        Object.entries(rule).forEach(([key, value]) => {
          rules.push({ key: `${key}-${rules.length}`, ...value });
        });
      });
      return rules;
    },

    loadFactor(producer) {
      const capacity = Number(producer.capacity_solar_panel);
      if (!capacity || capacity <= 0) {
        return null;
      }
      return Math.min(1, Math.max(0, (Number(producer.latest_power) || 0) / capacity));
    },

    delayClass(minutes) {
      if (minutes < 0 || minutes > 120) {
        return "over_5h";
      } else if (minutes > 60) {
        return "over_3h";
      } else if (minutes > 30) {
        return "over_1h";
      }
      return "fresh";
    },

    toggleExpanded(producerId) {
      this.expanded[producerId] = !this.expanded[producerId];
    },

    alarmType(criticality) {
      if (criticality === "crit") {
        return "error";
      } else if (criticality === "warn") {
        return "warning";
      }
      return "info";
    },

    applyData(data) {
      if (!data || typeof data !== "object") {
        this.item = null;
        return;
      }
      const name = Object.keys(data)[0];
      this.power_station_name = name;
      this.item = name !== undefined ? data[name] : null;
      if (this.producers.length === 1 && this.expanded[this.producers[0].id] === undefined) {
        this.expanded[this.producers[0].id] = true;
      }
    },

    refresh() {
      if (isNone(this.power_station_id)) {
        return;
      }
      this.loading = true;
      this.$store
        .dispatch("machines/get_solar_status_producer_items", { power_station_ids: [this.power_station_id] })
        .then((data) => {
          this.applyData(data);
        })
        .catch((error) => {
          console.error("Error fetching items:", error);
        })
        .finally(() => {
          this.loading = false;
          this.lastRefresh = moment();
        });
    }
  },

  created() {
    // A station handed over by another page is shown at once, then refreshed
    const preloaded = this.$store.state.machines.item;
    if (preloaded && Object.keys(preloaded).length) {
      this.applyData(preloaded);
      this.$store.dispatch("machines/setSelectedItem", null);
    }
    this.refresh();
  },

  mounted() {
    this.intervalId = window.setInterval(() => {
      this.refresh();
    }, 60 * 1000);
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      item: null,
      power_station_name: null,
      loading: false,
      lastRefresh: null,
      intervalId: undefined,
      expanded: {},
      range: "now-24h",
      GRAFANA_SOLAR_PANELS,
      GRAFANA_EMBED_ENABLED
    };
  }
};
</script>

<style scoped>
/* Freshness scale shared with the production page */
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}
.fresh-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}

/* Sticky header, pinned under the app bar while the page scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #15a9c9 55%, #f6b73c 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 8px;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff3d6;
  color: #e08a00;
}
.topbar__badge--on {
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__badge--off {
  background: #fde7e7;
  color: #d32f2f;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__sub {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}
.range-toggle {
  border-color: rgba(0, 0, 0, 0.12);
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
/* KPI tiles */
.kpi-row {
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--amber .kpi__badge {
  background: #fff3d6;
  color: #e08a00;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  min-height: 1.1em;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Producer cards */
.producer-card {
  padding: 14px 16px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.producer-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.producer-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: color-mix(in srgb, currentColor 12%, white);
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  background: color-mix(in srgb, currentColor 12%, white);
}
.status-pill__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.min-width-0 {
  min-width: 0;
}

.producer-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px 14px;
}
.stat--wide {
  grid-column: 1 / -1;
}
.stat__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stat__value {
  font-size: 1.1rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.stat__value--big {
  font-size: 1.5rem;
  font-weight: 700;
}
.stat__unit {
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

/* Grafana panels */
.chart-block {
  margin-top: 14px;
}
.graf-card {
  padding: 12px 16px 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.graf-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.graf-frame {
  display: block;
  width: 100%;
  height: 260px;
  border: 0;
  border-radius: 8px;
  background: #fafbfc;
}
.graf-frame--station {
  height: 320px;
}
.graf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 120px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 147, 191, 0.4);
  background: #f7fbfd;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: none;
}
.graf-placeholder:hover {
  background: #eef7fb;
}
.graf-placeholder--small {
  min-height: 90px;
}
.producer-card--single .graf-frame {
  height: 380px;
}

/* Telemetry table */
.tel-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.tel-table :deep(td) {
  font-variant-numeric: tabular-nums;
  font-size: 0.85rem;
}
.tel-table :deep(tr.tel-ok td) {
  background-color: rgba(76, 175, 80, 0.06);
}
.tel-table :deep(tr.tel-low td) {
  background-color: rgba(255, 152, 0, 0.08);
}

@media (max-width: 600px) {
  .producer-stats,
  .producer-card--single .producer-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  .producer-card--single .stat--wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 700px) {
  .graf-frame {
    height: 220px !important;
  }
  .topbar__actions {
    flex-wrap: wrap;
    gap: 6px;
  }
  .range-toggle {
    width: 100%;
  }
  .range-toggle .v-btn {
    flex: 1;
  }
}
</style>
